import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import uploadDocumentIcon from "../../assets/upload-documents-icon.png";
import pdfIcon from "../../assets/pdf.png";
import deleteIcon from "../../assets/delete-icon.png";
import { ChevronDown, ChevronsUpDown, Languages, Save } from 'lucide-react';
import { toggleCollapseFiles } from '../../store/reducers'; 
import { useLanguage, getUploadDocumentsTranslation } from '../../hooks/useLanguage';

function UploadDocuments() {
    const dispatch = useDispatch();
    const { documents, uploadComplete, uploadedFiles, hasDocuments, collapseFiles } = useSelector((state) => state.documents);
    const extractedData = useSelector(state => state.documents.extractedData);
    const [isDragging, setIsDragging] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState('');
    const fileInputRef = useRef(null);
    const [isSpanish] = useLanguage();
    
    const MAX_DOCUMENTS = 20;

    const processFiles = async (files) => {
        try {
            setIsProcessing(true);
            setError('');
            
            // Filter out files that already exist
            const existingFileNames = (uploadedFiles || []).map(file => file.name);
            const newFiles = Array.from(files).filter(file => !existingFileNames.includes(file.name));
            const duplicateFiles = Array.from(files).filter(file => existingFileNames.includes(file.name));
            
            if (duplicateFiles.length > 0) {
                const duplicateNames = duplicateFiles.map(f => f.name).join(', ');
                setError(`File(s) already exist: ${duplicateNames}`);
            }
            
            if (newFiles.length === 0) {
                setIsProcessing(false);
                return;
            }
            
            const currentCount = uploadedFiles ? uploadedFiles.length : 0;
            const newFilesCount = newFiles.length;
            
            if (currentCount + newFilesCount > MAX_DOCUMENTS) {
                setError(getUploadDocumentsTranslation('maxDocumentsError', isSpanish, MAX_DOCUMENTS, newFilesCount, currentCount));
                return;
            }
            
            dispatch({ 
                type: 'ADD_DOCUMENTS', 
                payload: newFiles
            });
            
            dispatch({ 
                type: 'SET_UPLOADED_FILES', 
                payload: [...uploadedFiles || [], ...newFiles]
            });
            
            dispatch({ type: 'UPLOAD_COMPLETE' });
            
        } catch (error) {
            console.error("Error processing files:", error);
            setError(getUploadDocumentsTranslation('errorProcessingFiles', isSpanish));
        } finally {
            setIsProcessing(false);
        }
    };

    const handleFileChange = (event) => {
        const selectedFiles = event.target.files;
        if (selectedFiles && selectedFiles.length > 0) {
            processFiles(selectedFiles);
        }
    };

    const handleDrop = (event) => {
        event.preventDefault();
        setIsDragging(false);
        const droppedFiles = event.dataTransfer.files;
        if (droppedFiles && droppedFiles.length > 0) {
            // Filter to only accept PDF files
            const pdfFiles = Array.from(droppedFiles).filter(file => file.type === 'application/pdf');
            if (pdfFiles.length > 0) {
                processFiles(pdfFiles);
            } else {
                setError(getUploadDocumentsTranslation('errorProcessingFiles', isSpanish));
            }
        }
    };

    const handleDragEnter = (event) => {
        event.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (event) => {
        event.preventDefault();
        setIsDragging(false);
    };

    const handleDelete = (fileName) => {
        dispatch({ 
            type: 'REMOVE_DOCUMENT', 
            payload: fileName 
        });
        
        dispatch({
            type: 'SET_UPLOADED_FILES',
            payload: uploadedFiles.filter(file => file.name !== fileName)
        });
        
        if (uploadedFiles.filter(file => file.name !== fileName).length === 0) {
            dispatch({ type: 'RESET_UPLOAD_STATUS' });
        }
        
        setError('');
    };

    const formatSizeInMB = (sizeInBytes) => {
        return (sizeInBytes / (1024 * 1024)).toFixed(2);
    };
    
    // Auto-remove error message after 10 seconds
    useEffect(() => {
        if (error) {
            const timer = setTimeout(() => {
                setError('');
            }, 1000000); // 10 seconds
            
            return () => clearTimeout(timer);
        }
    }, [error]);
    
    const remainingDocuments = MAX_DOCUMENTS - (uploadedFiles ? uploadedFiles.length : 0);
    
    const isUploadDisabled = remainingDocuments === 0 || isProcessing || extractedData;
    
    const getButtonText = () => {
        if (isProcessing) return getUploadDocumentsTranslation('processing', isSpanish);
        if (extractedData) return getUploadDocumentsTranslation('documentsProcessed', isSpanish);
        if (remainingDocuments === 0) return getUploadDocumentsTranslation('documentLimitReached', isSpanish);
        return getUploadDocumentsTranslation('selectFiles', isSpanish);
    };
    
    const getMessageText = () => {
        if (extractedData) return getUploadDocumentsTranslation('documentsSuccessfullyProcessed', isSpanish);
        if (remainingDocuments === 0) return getUploadDocumentsTranslation('pleaseRemoveSome', isSpanish);
        return null;
    };

    return (
        <div className="flex-1 flex flex-col">
            <div className="mb-4">
                <div className="circle bg-default mb-2">
                    <span className="text-white">1</span>
                </div>
                <h1 className="text-3xl font-bold mb-2">{getUploadDocumentsTranslation('uploadDocuments', isSpanish)}</h1>
                <p className="pr-8 text-gray-500">
                    {getUploadDocumentsTranslation('uploadDescription', isSpanish)}
                </p>
            </div>
            <div className="flex-col border-container p-4 rounded-xl flex-1 flex">
                <div
                    className={`flex flex-col justify-center flex-1 text-center border-dashed border-2 border-gray-400 p-8 rounded-md ${isDragging ? 'bg-secondary' : 'bg-white'} ${isUploadDisabled ? 'opacity-50' : ''}`}
                    onDrop={!isUploadDisabled ? handleDrop : (e) => e.preventDefault()}
                    onDragOver={(e) => e.preventDefault()}
                    onDragEnter={!isUploadDisabled ? handleDragEnter : (e) => e.preventDefault()}
                    onDragLeave={handleDragLeave}
                >
                    <input 
                        type="file" 
                        onChange={handleFileChange} 
                        className="hidden" 
                        accept="application/pdf"
                        multiple
                        ref={fileInputRef}
                        disabled={isUploadDisabled}
                    />
                    <button
                        onClick={() => !isUploadDisabled && fileInputRef.current.click()}
                        className={`select-file-button my-0 mx-auto py-4 px-8 rounded-full flex gap-4 items-center m-auto ${!isUploadDisabled ? 'bg-default' : extractedData ? 'bg-default' : 'bg-gray-400'}`}
                        disabled={isUploadDisabled}
                    >
                        <img src={uploadDocumentIcon} className="h-6 brightness-0 invert" alt="Upload icon" />
                        <p className="font-bold text-white truncate">
                            {getButtonText()}
                        </p>
                    </button>
                    
                    {!isUploadDisabled ? (
                        <>
                            <p className="mt-4 opacity-50">{getUploadDocumentsTranslation('or', isSpanish)}</p>
                            <p className="mt-4 truncate">{getUploadDocumentsTranslation('dragDrop', isSpanish)}</p>
                        </>
                    ) : (
                        <p className={`mt-4 ${extractedData ? 'text-default' : 'text-red-500'}`}>
                            {getMessageText()}
                        </p>
                    )}
                </div>

                {error && (
                    <div className="mt-4 p-3 bg-red-100 text-red-700 rounded-md">
                        {error}
                    </div>
                )}
                
                {uploadComplete && uploadedFiles && uploadedFiles.length > 0 && (
                    <div className="mt-4 overflow-auto">
                        <div className='flex items-center'>
                        <h2 className="text-xl font-bold mb-2">{getUploadDocumentsTranslation('uploadedFiles', isSpanish)}
                        <span className="text-sm text-gray-600 ml-2">
                        ({uploadedFiles ? uploadedFiles.length : 0}/{MAX_DOCUMENTS})
                    </span>
                        </h2>
                        <button
                          className={`flex items-center gap-2 py-2 px-2 ml-4 rounded-full hover:bg-secondary w-fit`}
                          onClick={() => dispatch(toggleCollapseFiles())}
                        >
                        <ChevronDown className={`size-5`} />
                        </button>
                        </div>
                        {!collapseFiles && (
                        <table className="min-w-full border-collapse text-sm">
                            <thead>
                                <tr>
                                    <th className="p-2 pl-0">{getUploadDocumentsTranslation('fileName', isSpanish)}</th>
                                    <th className="p-2">{getUploadDocumentsTranslation('size', isSpanish)}</th>
                                    <th className="p-2 text-center"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {uploadedFiles.map((file, index) => (
                                    <tr key={index} className="truncate">
                                        <td className="p-2 pl-0 truncate">
                                            <div className="flex gap-2 items-center">
                                                <img src={pdfIcon} className="h-8 w-8" alt="PDF icon" />
                                                <p className="truncate">{file.name}</p>
                                            </div>
                                        </td>
                                        <td className="p-2 truncate">{formatSizeInMB(file.size)}{getUploadDocumentsTranslation('mb', isSpanish)}</td>
                                        {hasDocuments && uploadComplete && !extractedData && (
                                        <td className="p-2">
                                            <button
                                                onClick={() => handleDelete(file.name)}
                                                className="text-red-500 m-auto flex h-6 w-6"
                                                title={getUploadDocumentsTranslation('delete', isSpanish)}
                                            >
                                                <img src={deleteIcon} className="h-6 w-6" alt={getUploadDocumentsTranslation('delete', isSpanish) + ' icon'} />
                                            </button>
                                        </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default UploadDocuments;